import { IEditorPlatformApplication_unsafe } from '../types';
import { Application } from './Application';
import {
  ApplicationsErrorCode,
  createApplicationsError,
} from './ApplicationsError';
import {
  PlatformPrivateEvent,
  PlatformPrivateEventEmitter,
} from '@wix/public-editor-platform-events';

export class Applications {
  private applications: Record<string, Application> = {};

  constructor(private events: PlatformPrivateEventEmitter) {
    this.subscribeOnPrivateEvents(events);
  }

  public subscribeOnPrivateEvents(events: PlatformPrivateEventEmitter) {
    events.subscribe((event) => {
      switch (event.type) {
        case PlatformPrivateEvent.ApplicationsSpecsReceived:
          for (const appDefinitionId of event.payload.appDefinitionIds) {
            this.init(appDefinitionId);
          }
          break;
      }
    });
  }

  private init(appDefinitionId: string) {
    this.applications[appDefinitionId] = new Application(appDefinitionId);
  }

  public async setApplication(app: IEditorPlatformApplication_unsafe) {
    await this.events?.withEvent(
      this.events.factories.createInitApplicationApiEvent(app.appDefinitionId),
      () => {
        if (!this.applications[app.appDefinitionId]) {
          this.init(app.appDefinitionId);
        }

        this.applications[app.appDefinitionId].setApplication(app);
      },
    );
  }

  private ensureApplication(appDefinitionId: string) {
    const app = this.applications[appDefinitionId];

    if (!app) {
      throw createApplicationsError(
        ApplicationsErrorCode.ApplicationDoesNotExist,
      ).withApplicationId(appDefinitionId);
    }

    return app;
  }

  /**
   * NOTE: called only at the WorkerManager
   * when iframe is connected to the infra
   */
  public async getPrivateAPI(appDefinitionId: string) {
    return this.events?.withEvent(
      this.events.factories.createGetApplicationApiEvent(
        appDefinitionId,
        'private',
      ),

      async () => {
        const app = this.ensureApplication(appDefinitionId);
        return app.getPrivateAPI();
      },
    );
  }

  /**
   * NOTE: called from private api namespace
   */
  public async getPublicAPIWhenAppIsReady(appDefinitionId: string) {
    return this.events?.withEvent(
      this.events.factories.createGetApplicationApiEvent(
        appDefinitionId,
        'public',
      ),
      async () => {
        const app = await this.ensureApplication(appDefinitionId).waitReady();
        return app.getPublicAPI();
      },
    );
  }
}
